import { useReducer, useEffect, useState, useCallback, useMemo } from 'react'
import { itineraryEditorReducer, EMPTY_EDITOR_STATE } from './itineraryEditorReducer'
import { useItineraryWeek, useSaveItineraryWeek, useScheduleAnalysis } from './useItinerary'
import { useUpdateCampaign, useCampaignDetail } from './useCampaign'
import { toaster } from '@/components/ui/toaster'
import { sameDayHeadsUp } from '../components/campaign/itinerary/doubleBooking'
import type {
  AssignmentMode,
  ItineraryAssignmentDto,
  ItineraryTurfPoolDto,
  ItineraryAssignmentUpsert,
  AvailabilityChange,
} from '../types/campaign'

function toLocalDateStr(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}
function getMonday(date: Date): string {
  const d = new Date(date)
  const day = d.getDay()
  const diff = d.getDate() - day + (day === 0 ? -6 : 1)
  d.setDate(diff)
  return toLocalDateStr(d)
}

export function useItineraryEditor(campaignId: string) {
  const [weekStart, setWeekStart] = useState(() => getMonday(new Date()))
  const [pendingWeekNav, setPendingWeekNav] = useState<string | null>(null)

  const { data: serverData, isLoading, error } = useItineraryWeek(campaignId, weekStart)
  const { data: campaignDetail } = useCampaignDetail(campaignId)
  const saveMutation = useSaveItineraryWeek(campaignId)
  const updateCampaignMutation = useUpdateCampaign(campaignId)

  const assignmentMode: AssignmentMode = serverData?.assignmentMode ?? 'Static'
  const isStatic = assignmentMode === 'Static'

  const scheduleRequest = useMemo(
    () => (serverData && !isStatic ? { weekStart } : null),
    [serverData, weekStart, isStatic],
  )
  const { data: scheduleData } = useScheduleAnalysis(campaignId, scheduleRequest)

  const [state, dispatch] = useReducer(itineraryEditorReducer, EMPTY_EDITOR_STATE)

  useEffect(() => {
    if (serverData) dispatch({ type: 'sync', payload: serverData.canvassers })
  }, [serverData])

  const isDirty = state.pendingUpserts.size > 0 || state.pendingDeletions.size > 0 || state.pendingAvailability.size > 0

  const poolTurfs = useMemo(() => serverData?.turfPool ?? [], [serverData])

  useEffect(() => {
    if (!isDirty) return
    const handler = (e: BeforeUnloadEvent) => { e.preventDefault() }
    window.addEventListener('beforeunload', handler)
    return () => window.removeEventListener('beforeunload', handler)
  }, [isDirty])

  // Same-day double-booking is an info toast only: it never blocks the assignment or Save, and
  // Static mode has no days to double-book.
  const headsUp = useCallback((grantId: string, dateStr: string, excludeAssignmentId?: string) => {
    if (isStatic) return
    const title = sameDayHeadsUp(state.canvassers, grantId, dateStr, excludeAssignmentId)
    if (title) toaster.create({ title, type: 'info', duration: 4000 })
  }, [isStatic, state.canvassers])

  const assignTurf = useCallback((turf: ItineraryTurfPoolDto, grantId: string, dateStr: string) => {
    headsUp(grantId, dateStr)
    dispatch({ type: 'assign', turf, grantId, dateStr })
  }, [headsUp])
  const moveAssignment = useCallback((assignment: ItineraryAssignmentDto, targetGrantId: string, targetDateStr: string) => {
    headsUp(targetGrantId, targetDateStr, assignment.id)
    dispatch({ type: 'move', assignment, targetGrantId, targetDateStr })
  }, [headsUp])
  const removeAssignment = useCallback((assignmentId: string) => {
    dispatch({ type: 'remove', assignmentId })
  }, [])
  const toggleAvailability = useCallback((grantId: string, dateStr: string, isAvailable: boolean) => {
    dispatch({ type: 'toggleAvailability', grantId, dateStr, isAvailable })
  }, [])

  const save = useCallback((opts?: { onSuccess?: () => void; onError?: () => void }) => {
    const assignments: ItineraryAssignmentUpsert[] = Array.from(state.pendingUpserts.values())
    const deletedAssignmentIds = Array.from(state.pendingDeletions)
    const availabilityChanges: AvailabilityChange[] = Array.from(state.pendingAvailability.values())
    saveMutation.mutate(
      { assignments, deletedAssignmentIds, availabilityChanges },
      { onSuccess: opts?.onSuccess, onError: opts?.onError },
    )
  }, [state.pendingUpserts, state.pendingDeletions, state.pendingAvailability, saveMutation])

  const navigateWeek = useCallback((target: string) => {
    if (isDirty) setPendingWeekNav(target)
    else setWeekStart(target)
  }, [isDirty])

  const goToPreviousWeek = useCallback(() => {
    const d = new Date(weekStart + 'T00:00:00'); d.setDate(d.getDate() - 7); navigateWeek(toLocalDateStr(d))
  }, [weekStart, navigateWeek])
  const goToNextWeek = useCallback(() => {
    const d = new Date(weekStart + 'T00:00:00'); d.setDate(d.getDate() + 7); navigateWeek(toLocalDateStr(d))
  }, [weekStart, navigateWeek])
  const goToToday = useCallback(() => navigateWeek(getMonday(new Date())), [navigateWeek])
  const confirmWeekNav = useCallback(() => {
    setPendingWeekNav(prev => { if (prev) setWeekStart(prev); return null })
  }, [])
  const cancelWeekNav = useCallback(() => setPendingWeekNav(null), [])

  const toggleMode = useCallback((mode: AssignmentMode, opts?: { onSuccess?: () => void; onError?: () => void; onBlockedDirty?: () => void }) => {
    if (mode === assignmentMode || !campaignDetail) return
    if (isDirty) { opts?.onBlockedDirty?.(); return }
    updateCampaignMutation.mutate(
      {
        label: campaignDetail.label,
        description: campaignDetail.description ?? undefined,
        status: campaignDetail.status,
        assignmentMode: mode,
        refreshStrategyOverride: campaignDetail.refreshStrategyOverride as any,
      },
      { onSuccess: opts?.onSuccess, onError: opts?.onError },
    )
  }, [assignmentMode, campaignDetail, updateCampaignMutation, isDirty])

  return {
    weekStart,
    weekStartDate: weekStart,
    assignmentMode,
    isStatic,
    canvassers: state.canvassers,
    poolTurfs,
    scheduleMap: scheduleData?.assignments,
    isLoading,
    error,
    isDirty,
    isSaving: saveMutation.isPending,
    isTogglingMode: updateCampaignMutation.isPending,
    assignTurf,
    moveAssignment,
    removeAssignment,
    toggleAvailability,
    save,
    goToPreviousWeek,
    goToNextWeek,
    goToToday,
    pendingWeekNav,
    confirmWeekNav,
    cancelWeekNav,
    toggleMode,
  }
}
